import Icon from '@components/v2/ui/Icon/Icon';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { CircleAlert, CircleCheck, Minus } from 'lucide-react';
import { ElementType } from 'react';
import useMoney from '../../../../../../../hooks/useMoney';
import { useTranslation } from '../../../../../../translation/TranslationContext';
import {
  BusinessPolicyCriterionStatus,
  BusinessPolicyViolationCriterionView,
} from '../businessPolicyViolation.types';
import { getCriterionValueLabel } from '../businessPolicyViolation.utils';
import BusinessPolicyViolationGroupList from './BusinessPolicyViolationGroupList';

interface BusinessPolicyViolationCriterionRowProps {
  criterion: BusinessPolicyViolationCriterionView;
}

interface StatusAppearance {
  icon: ElementType;
  color: string;
}

const APPEARANCE_BY_STATUS: Record<BusinessPolicyCriterionStatus, StatusAppearance> = {
  [BusinessPolicyCriterionStatus.VIOLATED]: { icon: CircleAlert, color: 'error.main' },
  [BusinessPolicyCriterionStatus.SATISFIED]: { icon: CircleCheck, color: 'success.main' },
  [BusinessPolicyCriterionStatus.IGNORED]: { icon: Minus, color: 'text.disabled' },
};

const BusinessPolicyViolationCriterionRow = ({
  criterion,
}: BusinessPolicyViolationCriterionRowProps) => {
  const { translate } = useTranslation();
  const { formatMoney } = useMoney();

  const appearance = APPEARANCE_BY_STATUS[criterion.status];
  const violatedGroups = criterion.groups.filter((group) => group.isViolated);
  const isViolated = criterion.status === BusinessPolicyCriterionStatus.VIOLATED;

  return (
    <Stack sx={{ gap: 1 }}>
      <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
        <Box sx={{ display: 'inline-flex', color: appearance.color }}>
          <Icon icon={appearance.icon} size="md" />
        </Box>

        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
          {criterion.measureLabel}{' '}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {criterion.comparisonLabel}
          </Box>
        </Typography>

        <Typography
          variant="body2"
          color={isViolated ? 'error.main' : 'text.secondary'}
          sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
        >
          {getCriterionValueLabel(criterion, { translate, formatMoney })}
        </Typography>
      </Stack>

      {violatedGroups.length > 0 && (
        <BusinessPolicyViolationGroupList
          groups={violatedGroups}
          withinCriterionCount={criterion.groups.length - violatedGroups.length}
          valueInput={criterion.valueInput}
        />
      )}
    </Stack>
  );
};

export default BusinessPolicyViolationCriterionRow;
